import { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Grid, Hidden, LinearProgress } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useHistory } from 'react-router';
import { FieldArray, Formik } from 'formik';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { DatePicker } from '../../lib/formik/DatePicker';
import { useDispatch } from 'react-redux';
import { SubHeading } from '../investors/components';
import MFSelectField from '../../lib/formik/SelectField';
import { getFundManagersList, getTrustessList } from '../../redux-store/actions/userManagement';
import {
  addFund,
  addPlan,
  fundAccess,
  getPlanList,
  updateFund,
} from '../../redux-store/actions/funds';
import { Location } from 'history';
import {
  accessType,
  addFundPlanType,
  ClassPlanProps,
  FundProps,
  MultiStampSeriesEntry,
} from '../../redux-store/types/funds';
import { addFundValidation, editFundValidation } from '../../utils/schema';
import {
  PlacementFeeApplicable,
  allowOnlyNumbers,
  currencyConversion,
  isFieldApplicable,
  isFieldTypeFixed,
  isFieldTypeVariable,
} from '../../utils/utilityFunctions';
import {
  CurrencyMaster,
  defaultCurrency,
  feeTypeMaster,
  flowTypeMaster,
  investmentPlanTypeMaster,
  yesNoMaster,
  IS_STAMP_PAPER_SERIES_MASTER,
  STAMP_SERIES_TYPE_MASTER,
  JOURNEY_TYPE,
} from '../../utils/constant';
import { DistributorListType, FundManager, Trustee } from '../../redux-store/types/api-types';
import { getDistributorsListForFilters } from '../../redux-store/actions';
import MFCheckbox from '../../lib/formik/Checkbox';
import { MultipleSelect } from '../../lib/formik/MultipleSelectField';
import UseRadioGroup from '../../lib/formik/Radio';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ConfirmationDialog } from '../commonComponents';

type NewPlanType = addFundPlanType & {
  isApplicableForDistributor: boolean;
};
const initialValues: NewPlanType = {
  schemeCode: '',
  schemeName: '',
  amcCode: '',
  bankName: '',
  bankBranch: '',
  ifscCode: '',
  bankAccountNumber: '',
  paymentApiKey: '',
  id: '',
  schemeId: null,
  planCode: '',
  planDescription: '',
  schemeNature: '',
  registrationNumber: '',
  schemeCategory: '',
  country: '',
  currency: defaultCurrency,
  transactionCurrency: defaultCurrency,
  schemeStartDate: `${new Date()}`,
  lockInPeriod: '',
  lockInExtension: '',
  inActive: true,
  faceValue: 0,
  fundAddress1: '',
  fundAddress2: '',
  fundAddress3: '',
  isActive: true,
  custodian: '',
  setupFee: 0,
  fundManagerId: null,
  trusteeId: null,
  minCommitmentAmount: null,
  maxCommitmentAmount: 0,
  minContributionPercentage: 0,
  managementFee: 0,
  tcLink: '',
  performanceFee: '',
  totalExpense: 0,
  placementFee: null,
  isPlacementFeeApplicable: '',
  hurdleRateOfReturns: '',
  leegalityStampSeries: '',
  leegalityStampSeriesGroup: '',
  leegalityStampValue: null,
  leegalityUseStampSeries: true,
  stampSeriesType: 'multi',
  multipleStampSeries: [
    {
      stampSeries: '',
      seriesQuantity: '',
    },
  ],
  distributorIds: null,
  isApplicableForDirect: false,
  isApplicableForDistributor: false,
  isInvestorWithPanApplicable: false,
  operatingExpenseApplicable: '',
  operatingExpenseInWords: '',
  exitLoadApplicable: '',
  exitLoadInWords: '',
  placementFeeType: '',
  minPlacementFee: null,
  maxPlacementFee: null,
  investmentPlanType: '',
  investmentPlanApplicable: '',
  isOnlyXalt: false,
  journeyType: '',
  isXorbit: false,
};

export default function AddFund({
  location,
}: {
  location: Location<{ fundId: number }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fundManager, setFundManager] = useState<any[]>([]);
  const [editField, setEditField] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const { fundId } = location.state || {};
  const [access, setAccess] = useState(false);
  const [trustee, setTrustee] = useState<any[]>([]);
  const [distributorList, setDistributorList] = useState<DistributorListType[]>([]);
  const [deleteStampIndex, setDeleteStampIndex] = useState<number | null>(null);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const trusteeList = (await dispatch(
          getTrustessList({ isActive: true })
        )) as unknown as Trustee[];
        const fundManagerList = (await dispatch(
          getFundManagersList({ isActive: true })
        )) as unknown as FundManager[];
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        setAccess(fundSetupAccess);
        setFundManager(fundManagerList);
        setTrustee(trusteeList);
        const res = (await dispatch(
          getDistributorsListForFilters()
        )) as unknown as DistributorListType[];
        setDistributorList(res);

        if (fundId) {
          const GetFundDetail = (await dispatch(
            getPlanList(fundId.toString())
          )) as unknown as FundProps;
          const {
            schemeCode,
            schemeName,
            amcCode,
            bankName,
            bankBranch,
            ifscCode,
            bankAccountNumber,
            paymentApiKey = '',
            isInvestorWithPanApplicable,
            leegalityStampSeries = '',
            leegalityStampSeriesGroup = '',
            leegalityStampValue = null,
            leegalityUseStampSeries = true,
            stampSeriesType = 'multi',
            multipleStampSeries = [],
            isOnlyXalt = false,
            journeyType = '',
            isXorbit = false,
          } = GetFundDetail || {};

          const normalizedMultipleStampSeries =
            stampSeriesType === 'multi' &&
            (!multipleStampSeries || multipleStampSeries.length === 0)
              ? [{ stampSeries: '', seriesQuantity: '' }]
              : multipleStampSeries;

          setEditField({
            ...editField,
            schemeCode,
            schemeName,
            amcCode,
            bankName,
            bankBranch,
            ifscCode,
            bankAccountNumber,
            paymentApiKey: String(paymentApiKey ?? ''),
            isInvestorWithPanApplicable,
            leegalityStampSeries,
            leegalityStampSeriesGroup,
            leegalityStampValue,
            leegalityUseStampSeries: true,
            stampSeriesType: 'multi',
            multipleStampSeries: normalizedMultipleStampSeries,
            isOnlyXalt,
            journeyType,
            isXorbit,
          });
        }
        if (!isComponentAlive) return;
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, []);

  const onSubmit = async (values: NewPlanType) => {
    try {
      const {
        schemeCode,
        schemeName,
        amcCode,
        bankName,
        bankBranch,
        ifscCode,
        bankAccountNumber,
        paymentApiKey,
        isInvestorWithPanApplicable,
        leegalityStampSeries = '',
        leegalityStampSeriesGroup = '',
        leegalityStampValue = null,
        leegalityUseStampSeries = null,
        stampSeriesType = 'single',
        multipleStampSeries = [],
        isOnlyXalt = false,
        journeyType = '',
        isXorbit = false,
      } = values;

      const {
        planCode,
        planDescription,
        schemeNature,
        registrationNumber,
        schemeCategory,
        country,
        currency,
        transactionCurrency,
        schemeStartDate,
        lockInPeriod,
        lockInExtension,
        inActive,
        faceValue,
        fundAddress1,
        fundAddress2,
        fundAddress3,
        isActive,
        custodian,
        setupFee,
        minCommitmentAmount,
        maxCommitmentAmount,
        minContributionPercentage,
        managementFee,
        fundManagerId,
        trusteeId,
        tcLink,
        performanceFee,
        totalExpense,
        hurdleRateOfReturns,
        distributorIds,
        isApplicableForDirect,
        placementFee = null,
        isPlacementFeeApplicable,
        operatingExpenseApplicable = '',
        operatingExpenseInWords = '',
        exitLoadApplicable = '',
        exitLoadInWords = '',
        placementFeeType = '',
        minPlacementFee = null,
        maxPlacementFee = null,
        investmentPlanType = '',
        investmentPlanApplicable = '',
      } = values;

      const fundPayload = {
        schemeCode,
        schemeName,
        amcCode,
        bankName,
        bankBranch,
        ifscCode,
        bankAccountNumber,
        paymentApiKey,
        isInvestorWithPanApplicable: String(isInvestorWithPanApplicable) === 'true',
        leegalityStampSeries:
          leegalityUseStampSeries?.toString() === 'true' && stampSeriesType === 'multi'
            ? null
            : leegalityStampSeries,
        leegalityStampSeriesGroup:
          leegalityUseStampSeries?.toString() === 'false' ? leegalityStampSeriesGroup : null,
        leegalityStampValue:
          leegalityUseStampSeries?.toString() === 'false' ? leegalityStampValue || null : null,
        leegalityUseStampSeries: leegalityUseStampSeries?.toString() === 'true' ? true : false,
        stampSeriesType: leegalityUseStampSeries?.toString() === 'true' ? stampSeriesType : null,
        multipleStampSeries:
          leegalityUseStampSeries?.toString() === 'true' && stampSeriesType === 'multi'
            ? multipleStampSeries
            : null,
        isOnlyXalt: String(isOnlyXalt) === 'true',
        journeyType,
        isXorbit: String(isXorbit) === 'true',
      };
      if (fundId) {
        await dispatch(updateFund(fundId, fundPayload));
      } else {
        const fundRes = (await dispatch(addFund(fundPayload))) as unknown as FundProps;

        await dispatch(
          addPlan({
            schemeId: fundRes.id,
            planCode,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency,
            transactionCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            fundManagerId,
            trusteeId,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee: setupFee ? setupFee : 0,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage: minContributionPercentage ? minContributionPercentage : 0,
            managementFee: managementFee ? managementFee : 0,
            tcLink,
            performanceFee,
            totalExpense,
            placementFee: isFieldApplicable(isPlacementFeeApplicable) ? placementFee : null,
            isPlacementFeeApplicable,
            hurdleRateOfReturns,
            distributorIds,
            isApplicableForDirect,
            operatingExpenseApplicable,
            operatingExpenseInWords: isFieldApplicable(operatingExpenseApplicable)
              ? operatingExpenseInWords
              : null,
            exitLoadApplicable,
            exitLoadInWords: isFieldApplicable(exitLoadApplicable) ? exitLoadInWords : null,
            placementFeeType: isFieldApplicable(isPlacementFeeApplicable) ? placementFeeType : null,
            minPlacementFee:
              isFieldApplicable(isPlacementFeeApplicable) && isFieldTypeVariable(placementFeeType)
                ? minPlacementFee
                : null,
            maxPlacementFee:
              isFieldApplicable(isPlacementFeeApplicable) && isFieldTypeVariable(placementFeeType)
                ? maxPlacementFee
                : null,
            investmentPlanType: isFieldApplicable(investmentPlanApplicable)
              ? investmentPlanType
              : null,
            investmentPlanApplicable,
          }) as unknown as ClassPlanProps
        );
      }
      history.push(fundId ? 'fund-details' : 'fund-list', { fundId: fundId });
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      <Formik
        initialValues={editField}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={fundId ? editFundValidation : addFundValidation}>
        {({ handleSubmit, values, setValues }) => {
          useEffect(() => {
            if (
              values.stampSeriesType === 'multi' &&
              (!values.multipleStampSeries || values.multipleStampSeries.length === 0)
            ) {
              setValues((prev: NewPlanType) => ({
                ...prev,
                multipleStampSeries: [{ stampSeries: '', seriesQuantity: '' }],
              }));
            }
          }, [values.stampSeriesType, values.multipleStampSeries?.length, setValues]);
          return (
            <Box
              sx={{
                bgcolor: 'white',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                borderRadius: '10px',
                py: { xs: 2, sm: 5 },
                pl: { xs: 0, sm: 5 },
                mt: { xs: 2, sm: 5 },
              }}
              component="form"
              noValidate
              onSubmit={handleSubmit}>
              <Hidden smUp={true}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    sx={{ height: 'fit-content', p: 0 }}
                    onClick={() =>
                      history.push(fundId ? 'fund-details' : 'fund-list', { fundId: fundId })
                    }>
                    <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: '#1C2D47',
                    }}>
                    Back
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    my: 2,
                    py: 2,
                    pl: { xs: 3, sm: 0 },
                    color: 'primary.main',
                    bgcolor: 'rgba(238, 244, 251, 0.5)',
                  }}>
                  <PersonOutlineOutlinedIcon fontSize="large" />
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      pl: 1,
                    }}>
                    Fund Setup
                  </Typography>
                </Box>
              </Hidden>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Hidden only="xs">
                  <IconButton
                    sx={{ height: 'fit-content', p: 0 }}
                    onClick={() =>
                      history.push(fundId ? 'fund-details' : 'fund-list', { fundId: fundId })
                    }>
                    <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                  </IconButton>
                </Hidden>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#1C2D47',
                    pl: { xs: 3, sm: 0 },
                  }}>
                  {fundId ? 'Edit Fund' : 'Add Fund'}
                </Typography>
              </Box>
              {loading ? (
                <LinearProgress sx={{ mt: 2 }} />
              ) : (
                <>
                  <Grid container rowSpacing={1} columnSpacing={4} px={2}>
                    <SubHeading
                      sx={{
                        color: 'common.blue',
                        letterSpacing: 0.5 + 'px',
                        padding: { xs: '10px 10px', sm: '10px 30px' },
                        ml: { xs: 3.5 },
                        // backgroundColor: 'red',
                      }}>
                      {'Fund Details(Scheme)'}
                    </SubHeading>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="schemeCode"
                        label="Scheme Code *"
                        placeholder="Enter Scheme Code"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="schemeName"
                        label="Scheme Name *"
                        placeholder="Enter Scheme Name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField name="amcCode" label="AMC Code" placeholder="Enter AMC Code" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="bankName"
                        label="Bank Name"
                        placeholder="Enter Bank Name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="bankBranch"
                        label="Bank Branch"
                        placeholder="Enter Bank Branch"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="ifscCode"
                        label="IFSC Code"
                        placeholder="Enter IFSC Code"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="bankAccountNumber"
                        label="Bank Account Number"
                        placeholder="Enter Bank Account Number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="paymentApiKey"
                        label="Payment API Key *"
                        placeholder="Enter Payment API Key"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} mt={2}>
                      <UseRadioGroup
                        formLabel="Flow Type *"
                        name={`isInvestorWithPanApplicable`}
                        items={Object.keys(flowTypeMaster).map((ele) => ({
                          label: flowTypeMaster[ele],
                          value: ele,
                        }))}
                        value={values.isInvestorWithPanApplicable?.toString()}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}>
                      <UseRadioGroup
                        name={`leegalityUseStampSeries`}
                        formLabel="Use Stamp Paper Series *"
                        items={Object.keys(IS_STAMP_PAPER_SERIES_MASTER).map((item) => ({
                          label: IS_STAMP_PAPER_SERIES_MASTER[item],
                          value: item,
                        }))}
                        // disabled
                        value={values.leegalityUseStampSeries?.toString()}
                      />
                    </Grid>
                    {values.leegalityUseStampSeries?.toString() === 'true' && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}>
                        <UseRadioGroup
                          name={`stampSeriesType`}
                          formLabel="Stamp Series Type *"
                          items={Object.keys(STAMP_SERIES_TYPE_MASTER).map((item) => ({
                            label: STAMP_SERIES_TYPE_MASTER[item],
                            value: item,
                          }))}
                          value={values.stampSeriesType || undefined}
                          // disabled
                        />
                      </Grid>
                    )}
                    {values.leegalityUseStampSeries?.toString() === 'true' &&
                      values.stampSeriesType === 'single' && (
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="leegalityStampSeries"
                            label="Stamp Paper Series *"
                            placeholder="Enter Stamp Paper Series"
                          />
                        </Grid>
                      )}
                    {values.leegalityUseStampSeries?.toString() === 'false' && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="leegalityStampSeriesGroup"
                            label="Stamp Paper Group *"
                            placeholder="Enter Stamp Paper Group"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="leegalityStampValue"
                            label="Stamp Paper Value *"
                            placeholder="Enter Stamp Paper Value"
                            type="number"
                            onKeyDown={(e) => {
                              allowOnlyNumbers(e);
                            }}
                            trimOnBlur={false}
                          />
                        </Grid>
                      </>
                    )}

                    {values.leegalityUseStampSeries?.toString() === 'true' &&
                      values.stampSeriesType === 'multi' && (
                        <Grid item xs={12}>
                          <Box sx={{ mt: 2 }}>
                            <FieldArray name="multipleStampSeries">
                              {({ push }) => (
                                <>
                                  {values.multipleStampSeries?.map((item, index) => (
                                    <Grid
                                      container
                                      rowSpacing={1}
                                      columnSpacing={2}
                                      key={index}
                                      sx={{ px: 2, mt: 1 }}
                                      alignItems="flex-start">
                                      <Grid item xs={12} sm={5} md={4}>
                                        <MFTextField
                                          name={`multipleStampSeries[${index}].stampSeries`}
                                          label={`Series Number ${index + 1} *`}
                                          placeholder="Enter Series Number"
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={7} md={8}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                          }}>
                                          <Box sx={{ flex: 0, minWidth: 260, maxWidth: 320 }}>
                                            <MFTextField
                                              name={`multipleStampSeries[${index}].seriesQuantity`}
                                              label="Total Stamps To Use *"
                                              placeholder="Enter Total Stamps"
                                              type="number"
                                              onKeyDown={(e) => {
                                                allowOnlyNumbers(e);
                                              }}
                                              trimOnBlur={false}
                                            />
                                          </Box>
                                          {index + 1 ===
                                            (values.multipleStampSeries?.length || 0) &&
                                            (values.multipleStampSeries?.length || 0) < 5 && (
                                              <IconButton
                                                onClick={() =>
                                                  push({
                                                    stampSeries: '',
                                                    seriesQuantity: '',
                                                  })
                                                }
                                                sx={{
                                                  p: 0,
                                                  color: 'rgba(0,0,0,0.7)',
                                                  ':hover': {
                                                    bgcolor: 'transparent',
                                                    cursor: 'pointer',
                                                  },
                                                }}>
                                                <AddIcon fontSize="small" />
                                              </IconButton>
                                            )}
                                          {(values.multipleStampSeries?.length || 0) > 1 && (
                                            <IconButton
                                              onClick={() => setDeleteStampIndex(index)}
                                              sx={{
                                                p: 0,
                                                color: 'rgba(0,0,0,0.7)',
                                                ':hover': {
                                                  bgcolor: 'transparent',
                                                  cursor: 'pointer',
                                                },
                                              }}>
                                              <RemoveIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  ))}
                                </>
                              )}
                            </FieldArray>
                          </Box>
                        </Grid>
                      )}
                    <Grid item xs={12} sm={6} md={4}>
                      <UseRadioGroup
                        formLabel="Journey Type *"
                        name="journeyType"
                        items={Object.keys(JOURNEY_TYPE).map((key) => ({
                          label: JOURNEY_TYPE[key],
                          value: key,
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} mt={2}>
                      <MFCheckbox
                        name="isOnlyXalt"
                        label="Only Xalt"
                        checked={String(values.isOnlyXalt) === 'true'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} mt={2}>
                      <MFCheckbox
                        name="isXorbit"
                        label="Only Xorbit"
                        checked={String(values.isXorbit) === 'true'}
                      />
                    </Grid>
                  </Grid>
                  {!fundId && (
                    <Grid container rowSpacing={1} columnSpacing={4} px={2}>
                      <SubHeading
                        sx={{
                          color: 'common.blue',
                          letterSpacing: 0.5 + 'px',
                          padding: { xs: '10px 10px', sm: '10px 30px' },
                          ml: { xs: 3.5 },
                        }}>
                        {'Plan Details'}
                      </SubHeading>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="planCode"
                          label="Plan Code *"
                          placeholder="Enter Plan Code"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="planDescription"
                          label="Plan Description *"
                          placeholder="Enter Plan Description"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="schemeNature"
                          label="Scheme Nature *"
                          placeholder="Enter Scheme Nature"
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="registrationNumber"
                        label="Registration Number *"
                        placeholder="Enter Registration Number"
                      />
                    </Grid> */}
                      <Grid item xs={12} sm={6} md={4}>
                        <MFSelectField
                          name="fundManagerId"
                          label="Investment Manager Name *"
                          items={fundManager.map((item) => ({
                            key: item.name,
                            value: item.id,
                          }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFSelectField
                          name="trusteeId"
                          label="Trustee Name *"
                          items={trustee.map((item) => ({
                            key: item.name,
                            value: item.id,
                          }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="schemeCategory"
                          label="Scheme Category *"
                          placeholder="Enter Scheme Category"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField name="country" label="Country" placeholder="Enter Country" />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFSelectField
                          name="currency"
                          label="Currency"
                          items={Object.keys(CurrencyMaster).map((item) => ({
                            key: CurrencyMaster[item].currency,
                            value: item,
                          }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFSelectField
                          name="transactionCurrency"
                          label="Transaction Currency"
                          items={Object.keys(CurrencyMaster).map((item) => ({
                            key: CurrencyMaster[item].currency,
                            value: item,
                          }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <DatePicker
                          label={'Scheme Start Date'}
                          inputLabelStyles={{
                            transform: 'unset',
                            fontSize: 14,
                            fontWeight: 500,

                            color: 'rgba(0,0,0,0.7)',
                          }}
                          placeholder={'Enter Scheme Start Date'}
                          name={`schemeStartDate`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="lockInPeriod"
                          label="Lock In Period(In Year)"
                          placeholder="Enter Lock In Period(In Year)"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="lockInExtension"
                          label="Lock In Extension(In Year)"
                          placeholder="Enter Lock In Extension(In Year)"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="faceValue"
                          label="Face value"
                          placeholder="Enter Face value"
                          type="number"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          trimOnBlur={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="fundAddress1"
                          label="Fund Address 1"
                          placeholder="Enter Fund Address 1"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="fundAddress2"
                          label="Fund Address 2"
                          placeholder="Enter Fund Address 2"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="fundAddress3"
                          label="Fund Address 3"
                          placeholder="Enter Fund Address 3"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="custodian"
                          label="Custodian"
                          placeholder="Enter Custodian"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="setupFee"
                          label="Setup Fee"
                          placeholder="Enter Setup Fee"
                          type="number"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          trimOnBlur={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="minCommitmentAmount"
                          label="Minimum Capital Commitment *"
                          placeholder="Enter Minimum Capital Commitment"
                          type="number"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          trimOnBlur={false}
                        />
                        {values.minCommitmentAmount != 0 && (
                          <Typography sx={{ fontSize: 13 }}>
                            {currencyConversion(values.minCommitmentAmount, values.currency)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="maxCommitmentAmount"
                          label="Maximum Capital Commitment *"
                          placeholder="Enter Maximum Capital Commitment"
                          type="number"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          trimOnBlur={false}
                        />
                        {values.maxCommitmentAmount != 0 && (
                          <Typography sx={{ fontSize: 13 }}>
                            {currencyConversion(values.maxCommitmentAmount, values.currency)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="minContributionPercentage"
                          label="Minimum Contribution Percentage"
                          placeholder="Enter Minimum Contribution Percentage"
                          type="number"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          trimOnBlur={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="managementFee"
                          label="Management Fee *"
                          placeholder="Enter Management Fee"
                          type="number"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          trimOnBlur={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name={'hurdleRateOfReturns'}
                          label="Transaction Fee %"
                          placeholder="Enter Transaction Fee %"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name={'performanceFee'}
                          label="Performance Fee %"
                          placeholder="Enter Performance Fee %"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name={'totalExpense'}
                          label="Total Expense % *"
                          placeholder="Total Expense %"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          type="number"
                          trimOnBlur={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} mt={2}>
                        <UseRadioGroup
                          formLabel="Placement Fee Applicable *"
                          name={`isPlacementFeeApplicable`}
                          items={Object.keys(yesNoMaster).map((ele) => ({
                            label: yesNoMaster[ele],
                            value: ele,
                          }))}
                        />
                      </Grid>
                      {isFieldApplicable(values.isPlacementFeeApplicable) && (
                        <>
                          <Grid item xs={12} sm={6} md={4} mt={2}>
                            <UseRadioGroup
                              formLabel="Placement Fee Type *"
                              name={`placementFeeType`}
                              items={Object.keys(feeTypeMaster).map((ele) => ({
                                label: feeTypeMaster[ele],
                                value: ele,
                              }))}
                            />
                          </Grid>
                          {/* {isFieldTypeFixed(values.placementFeeType) && ( */}
                          <Grid item xs={12} sm={6} md={4}>
                            <MFTextField
                              name={'placementFee'}
                              label="Placement Fee % *"
                              placeholder="Enter Placement Fee %"
                              onKeyDown={(e) => {
                                allowOnlyNumbers(e);
                              }}
                              type="number"
                              trimOnBlur={false}
                            />
                          </Grid>
                          {/* )} */}
                          {isFieldTypeVariable(values.placementFeeType) && (
                            <>
                              <Grid item xs={12} sm={6} md={4}>
                                <MFTextField
                                  name={'minPlacementFee'}
                                  label="Min Placement Fee % *"
                                  placeholder="Enter Min Placement Fee %"
                                  onKeyDown={(e) => {
                                    allowOnlyNumbers(e);
                                  }}
                                  type="number"
                                  trimOnBlur={false}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <MFTextField
                                  name={'maxPlacementFee'}
                                  label="Max Placement Fee % *"
                                  placeholder="Enter Max Placement Fee %"
                                  onKeyDown={(e) => {
                                    allowOnlyNumbers(e);
                                  }}
                                  type="number"
                                  trimOnBlur={false}
                                />
                              </Grid>
                            </>
                          )}
                        </>
                      )}
                      <Grid item xs={12} sm={6} md={4} mt={2}>
                        <UseRadioGroup
                          formLabel="Operating Expense Applicable *"
                          name={`operatingExpenseApplicable`}
                          items={Object.keys(yesNoMaster).map((ele) => ({
                            label: yesNoMaster[ele],
                            value: ele,
                          }))}
                        />
                      </Grid>
                      {isFieldApplicable(values.operatingExpenseApplicable) && (
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name={'operatingExpenseInWords'}
                            label="Operating Expense *"
                            placeholder="Enter operating expense"
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={4} mt={2}>
                        <UseRadioGroup
                          formLabel="Exit Load Applicable *"
                          name={`exitLoadApplicable`}
                          items={Object.keys(yesNoMaster).map((ele) => ({
                            label: yesNoMaster[ele],
                            value: ele,
                          }))}
                        />
                      </Grid>
                      {isFieldApplicable(values.exitLoadApplicable) && (
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name={'exitLoadInWords'}
                            label="Exit Load *"
                            placeholder="Enter exit load"
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6} md={4} mt={2}>
                        <UseRadioGroup
                          formLabel="Investment Plan Applicable *"
                          name={`investmentPlanApplicable`}
                          items={Object.keys(yesNoMaster).map((ele) => ({
                            label: yesNoMaster[ele],
                            value: ele,
                          }))}
                        />
                      </Grid>
                      {isFieldApplicable(values.investmentPlanApplicable) && (
                        <Grid item xs={12} sm={6} mt={2} md={4}>
                          <UseRadioGroup
                            formLabel="Investment Plan *"
                            name={`investmentPlanType`}
                            items={Object.keys(investmentPlanTypeMaster).map((ele) => ({
                              label: investmentPlanTypeMaster[ele],
                              value: ele,
                            }))}
                            value={values.investmentPlanType || ''}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name="tcLink"
                          label={`T&C Link`}
                          placeholder={`Enter T&C Link`}
                        />
                      </Grid>
                      {/* empty grid for proper alignment */}
                      <Grid xs={12}></Grid>
                      <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex' }}>
                          <MFCheckbox name={`isApplicableForDirect`} label="Direct" />
                          <MFCheckbox
                            name={`isApplicableForDistributor`}
                            label="Distributor"
                            onChange={({ target: { checked } }) => {
                              setValues({
                                ...values,
                                isApplicableForDistributor: checked,
                                distributorIds: [],
                              });
                            }}
                            checked={values.isApplicableForDistributor}
                          />
                        </Box>
                        {values.isApplicableForDistributor && (
                          <MultipleSelect
                            name="distributorIds"
                            label="Applicable Distributor(s) *"
                            items={distributorList.map((item) => ({
                              key: item.id,
                              value: `${item.arnCode || 'N/A'} - ${item.name} (${item.type})`,
                            }))}
                            renderText="Selected Distributor(s)"
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}
                  {access && (
                    <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 3 }}>
                      <MFSubmitButton label="Save" />
                    </Box>
                  )}
                  <ConfirmationDialog
                    message={'Are you sure you want to delete this Stamp Series?'}
                    open={deleteStampIndex !== null}
                    setOpen={() => setDeleteStampIndex(null)}
                    onSave={() => {
                      if (
                        deleteStampIndex !== null &&
                        (values.multipleStampSeries?.length || 0) > 1
                      ) {
                        const updated =
                          values.multipleStampSeries?.filter(
                            (_item, idx) => idx !== deleteStampIndex
                          ) || [];
                        setValues({
                          ...values,
                          multipleStampSeries: updated,
                        } as NewPlanType);
                      }
                      setDeleteStampIndex(null);
                    }}
                    onCancel={() => setDeleteStampIndex(null)}
                  />
                </>
              )}
            </Box>
          );
        }}
      </Formik>
    </>
  );
}
